import {noop} from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {FrontClickEvent} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {DataEventActionsEnum} from '../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {OnboardingDataEventObject} from '../../../../../libs/shared/core/src/models/tracking/objectNames/onboardingDataEventObject';
import {useDataEventTracking} from '../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {InfoBubblePillVariantsEnum} from '../../../../../libs/shared/ui/src/components/tip/infoBubblePill';
import heroImage from '../../../../assets/images/sandboxTip.svg';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {getIsSandboxGeneralInboxTicketingEnabled} from '../../../../store/data/inboxes/inboxesSelectors';
import {EducationTip} from '../../../educationTips/educationTip';
import {SandboxEducationTipProps} from './sandboxEducationTip';

/*
 * Props.
 */

interface WelcomeEducationTipProps extends SandboxEducationTipProps {}

/*
 * Styles.
 */

const StyledImg = styled.img`
  display: block;
  width: 380px;
  height: 142px;
`;

/*
 * Component.
 */

export const WelcomeEducationTip: FC<WelcomeEducationTipProps> = (props) => {
  const {trackDataEvent} = useDataEventTracking();

  const onStartClick = (event: FrontClickEvent) => {
    trackDataEvent(DataEventActionsEnum.CLICK, OnboardingDataEventObject.DEMO_WELCOME_START, {});
    props.onNextClick(event);
  };

  const onSkipClick = (event: FrontClickEvent) => {
    trackDataEvent(DataEventActionsEnum.CLICK, OnboardingDataEventObject.DEMO_WELCOME_SKIP, {});
    props.onEndClick(event, {shouldAdvanceStep: false});
  };

  const isGeneralSandboxInboxTicketing = useAppSelector(getIsSandboxGeneralInboxTicketingEnabled);

  return (
    <EducationTip
      anchor={props.anchor}
      position="right-start"
      isInteractive
      isVisible={props.isVisible}
      onRequestClose={noop}
      hero={<StyledImg src={heroImage} alt="" />}
      pillType={InfoBubblePillVariantsEnum.EDUCATION}
      title={
        isGeneralSandboxInboxTicketing ? (
          <Trans id="tip-onboarding-start-title-ticketing-v5">Learn how to resolve inquiries faster</Trans>
        ) : (
          <Trans id="tip-onboarding-start-title-v5">
            Learn how to use Front faster than sending one email
          </Trans>
        )
      }
      primaryActionLabel={<Trans id="tip-onboarding-start-start-v5">Show me</Trans>}
      onPrimaryClick={onStartClick}
      secondaryAction={{
        label: <Trans id="tip-onboarding-start-skip-v5">No thanks</Trans>,
        onClick: onSkipClick,
      }}
      dataObjectName={OnboardingDataEventObject.DEMO_WELCOME}
      overlayOffset={0}
    >
      {isGeneralSandboxInboxTicketing ? (
        <Trans id="tip-onboarding-start-description-ticketing-v5">
          Within 1 minute, you will learn how to use Front as your ticketing system.
        </Trans>
      ) : (
        <Trans id="tip-onboarding-start-description-v5">
          Give us two minutes, and we&apos;ll give you the tools you need to set your team up for success in
          Front.
        </Trans>
      )}
    </EducationTip>
  );
};
